// ─────────────────────────────────────────────────────────────────────────
// FinanceRecordsTable — enterprise list table for Billing / Payroll rows,
// plus the cell primitives the host composes its columns from.
// Server-side pagination is rendered with the shared CustomPagination.
// ─────────────────────────────────────────────────────────────────────────
import { Table } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import CustomPagination from '../CustomPagination.jsx';
import { FinanceEmptyState } from './FinanceComponents.jsx';
import { computeMarginBand, formatMoney, initialsOf } from './financeUtils';
import './finance-components.css';

// ── Cell primitives ────────────────────────────────────────────────────────

const AVATAR_TONES = ['blue', 'indigo', 'purple', 'green'];

function avatarTone(name) {
  const text = String(name ?? '');
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length];
}

export function FinanceAvatar({ name, size = 'md' }) {
  return (
    <span className={`fin-avatar fin-avatar--${avatarTone(name)} fin-avatar--${size}`}>
      {initialsOf(name)}
    </span>
  );
}

// `link` marks this cell as the row's "click to open" column — styles the
// primary text the same blue the row chevron turns on hover, so a clickable
// row reads as one before the user even hovers it. Only the first/name-like
// column on a clickable table should set it.
export function FinanceEmployeeCell({ name, secondary, link = false }) {
  return (
    <div className="fin-cell-employee">
      <FinanceAvatar name={name} />
      <span className="fin-cell-employee__text">
        <strong className={`fin-cell__primary${link ? ' fin-cell__primary--link' : ''}`}>{name || '—'}</strong>
        {secondary ? <span className="fin-cell__secondary">{secondary}</span> : null}
      </span>
    </div>
  );
}

export function FinanceTextCell({ primary, secondary, link = false }) {
  return (
    <div className="fin-cell-text">
      <span className={`fin-cell__primary${link ? ' fin-cell__primary--link' : ''}`}>{primary || '—'}</span>
      {secondary ? <span className="fin-cell__secondary">{secondary}</span> : null}
    </div>
  );
}

export function FinanceMoneyCell({ value, sub, strong = false }) {
  return (
    <div className="fin-cell-money">
      <span className={`fin-cell-money__value${strong ? ' fin-cell-money__value--strong' : ''}`}>
        {typeof value === 'number' ? formatMoney(value) : value ?? '—'}
      </span>
      {sub ? <span className="fin-cell__secondary">{sub}</span> : null}
    </div>
  );
}

export function FinanceBadge({ children, tone = 'neutral', title }) {
  return (
    <span className={`fin-badge fin-badge--${tone}`} title={title}>
      {children}
    </span>
  );
}

export function FinanceMarginBadge({ margin, marginPercent }) {
  const band = computeMarginBand(marginPercent);
  const tone = band === 'high' ? 'success' : band === 'low' ? 'danger' : 'neutral';
  return (
    <div className="fin-cell-margin">
      <FinanceBadge tone={tone}>{Number(marginPercent ?? 0).toFixed(1)}%</FinanceBadge>
      {margin != null ? <span className="fin-cell__secondary">{formatMoney(margin)}</span> : null}
    </div>
  );
}

// Trailing chevron cell the host appends as its last column to signal row
// navigation: { key: 'chevron', width: 44, render: () => <FinanceRowChevron /> }
export function FinanceRowChevron() {
  return <RightOutlined className="fin-table__chevron" />;
}

// ── Skeleton ───────────────────────────────────────────────────────────────

function SkeletonRows({ rows = 8 }) {
  return (
    <div className="fin-table-skeleton">
      {Array.from({ length: rows }, (_, i) => (
        <div className="fin-table-skeleton__row" key={i}>
          <span className="fin-skeleton fin-skeleton--avatar" />
          <span className="fin-skeleton fin-skeleton--lg" />
          <span className="fin-skeleton fin-skeleton--md" />
          <span className="fin-skeleton fin-skeleton--md" />
          <span className="fin-skeleton fin-skeleton--sm" />
          <span className="fin-skeleton fin-skeleton--pill" />
        </div>
      ))}
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────

export default function FinanceRecordsTable({
  columns,
  rows = [],
  rowKey = 'timesheetId',
  loading = false,
  onRowClick,
  page,
  limit,
  total = 0,
  onPageChange,
  onLimitChange,
  emptyState,
  scrollX = 1100,
}) {
  if (loading && !rows.length) return <SkeletonRows />;

  if (!loading && !rows.length) {
    return (
      <div className="fin-table-card">
        <FinanceEmptyState
          title="No records found"
          description="Try widening the month filter or clearing quick filters — finance records live in their timesheet's month."
          {...(emptyState ?? {})}
        />
      </div>
    );
  }

  return (
    <div className="fin-table-card">
      <Table
        className="fin-table"
        size="small"
        columns={columns}
        dataSource={rows}
        rowKey={rowKey}
        loading={loading}
        pagination={false}
        sticky
        scroll={{ x: scrollX }}
        onRow={(row) => ({
          onClick: (event) => {
            // Full-row navigation, but never hijack clicks on interactive children.
            if (event.target.closest('button, a, input, .fin-copy')) return;
            onRowClick?.(row);
          },
        })}
        rowClassName={() => (onRowClick ? 'fin-table__row fin-table__row--clickable' : 'fin-table__row')}
      />
      {total > 0 && page != null ? (
        <div className="fin-table__pagination">
          <CustomPagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={(next) => onPageChange?.(next)}
            onPageSizeChange={(size) => onLimitChange?.(size)}
          />
        </div>
      ) : null}
    </div>
  );
}
